import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { buttonClasses } from './ui/Button.jsx';
import { getAreaLabel, getNavGroups, isNavItemActive } from '../config/dashboardNav';

/**
 * Seitennavigation des Dashboards (ab `md:`; auf dem Telefon uebernimmt
 * `dashboards/DashboardBottomNav.js`).
 *
 * September 2026 umgebaut. Vorher: eine Ueberschrift "Dashboard Navigation"
 * (ein Meta-Label ueber sich selbst), darunter bis zu elf gleich aussehende
 * Eintraege ohne Gliederung — und ganz am Ende, hinter einer Trennlinie und in
 * derselben gedaempften Farbe wie ein inaktiver Menuepunkt, "Zurueck zur App".
 * Beim Onboarding einer Pilotschule hat ein neuer Schul-Admin genau deshalb
 * lange gesucht: Der Ausgang sah aus wie ein weiterer Menuepunkt und stand auf
 * einem 1080p-Schirm oft schon unter der Sichtkante.
 *
 * Jetzt beantwortet der Kopf zuerst "wo bin ich?" (Bereich + Schule), dann
 * kommt der Ausgang als Knopf — nicht als Menuepunkt —, und erst danach die
 * Ziele, in benannten Gruppen. Das Schubfach fuer das Telefon ist ersatzlos
 * entfallen, mit ihm der handgebaute Fokus-/Escape-/Scroll-Sperr-Code.
 */
const DashboardNavigation = () => {
  const location = useLocation();
  const { user, getUserRole } = useAuth();
  const userRole = getUserRole();

  const groups = getNavGroups(userRole);
  const areaLabel = getAreaLabel(userRole);
  const schoolName = user?.schoolName || '';

  // Zeilenhoehe: 44px als Tipp-Ziel, denn `md:` beginnt beim iPad im Hochformat
  // (768px) — dort ist die Seitenleiste beruehrbar. Erst ab `lg:` (Maus) wird es
  // enger. Ohne das steht die letzte Gruppe auf einem 900px hohen Fenster unter
  // der Sichtkante, und dann waere die Gliederung wieder nur eine Scrollaufgabe.
  const linkClass = (active) => `flex items-center min-h-[44px] lg:min-h-[38px] px-4 py-2 text-base md:text-sm rounded-xl transition-colors ${
    active
      ? 'bg-primary-soft text-primary font-medium'
      : 'text-muted-foreground hover:bg-foreground/5'
  }`;

  return (
    <nav aria-label="Dashboard-Navigation" className="flex flex-col bg-surface p-4 rounded-xl shadow-token-sm">
      {/* Kontextkopf: Bereichsname in Versalien, darunter die Schule. Poppins,
          nicht Bungee — Bungee ist Highlights vorbehalten (CD v2.0). */}
      {areaLabel && (
        <div className="px-4 pb-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{areaLabel}</p>
          {schoolName && (
            <p className="mt-0.5 text-sm font-semibold text-foreground truncate" title={schoolName}>{schoolName}</p>
          )}
        </div>
      )}

      {/* Der Ausgang, ganz oben und als Knopf. Genau die Verwechslung, um die es
          ging: Als Listeneintrag am Ende war er von einem Menuepunkt nicht zu
          unterscheiden. `secondary` statt `primary` — lila bleibt dem aktiven
          Ziel vorbehalten. */}
      <Link to="/app" className={buttonClasses('secondary', 'md', 'justify-start gap-2', true)}>
        <Home className="w-4 h-4 shrink-0" aria-hidden="true" />
        Zur App
      </Link>

      <div className="border-t border-border mt-4 pt-4 space-y-3">
        {groups.map((group, index) => (
          <div key={group.label || `group-${index}`} role="group" aria-label={group.label || undefined}>
            {group.label && (
              <h3 className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </h3>
            )}
            <div className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isNavItemActive(location.pathname, item);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={active ? 'page' : undefined}
                    className={linkClass(active)}
                  >
                    <Icon className="w-5 h-5 mr-3 shrink-0" aria-hidden="true" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </nav>
  );
};

export default DashboardNavigation;
